/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../logging/Log";
import { WebSocketManager } from "./WebSocketManager";

/**
 * Module-level singleton holder for the shared WebSocketManager instance.
 *
 * The primary connection is created by PostSigninInteractor.connectWebSocket()
 * after sign-in completes and other consumers observe this shared instance.
 *
 * **REQ-FSM-01:** Ensures a single active connection lifecycle across the app.
 * **REQ-LIFE-04:** No concurrent connection attempts — one manager at a time.
 */
export class WebSocketConnectionHolder {
  private static readonly TAG: string = "WebSocketConnectionHolder";

  private static mInstance: WebSocketManager | null = null;

  /**
   * Get the shared WebSocketManager instance.
   *
   * @returns The active manager, or null if no connection has been established.
   */
  public static getInstance(): WebSocketManager | null {
    return WebSocketConnectionHolder.mInstance;
  }

  /**
   * Store the shared WebSocketManager instance.
   * Destroys any previously held instance before replacing.
   *
   * @param manager - The new WebSocketManager to hold
   */
  public static setInstance(manager: WebSocketManager): void {
    if (WebSocketConnectionHolder.mInstance !== null) {
      Log.d(
        WebSocketConnectionHolder.TAG,
        "setInstance(): Destroying previous WebSocketManager"
      );
      WebSocketConnectionHolder.mInstance.destroy();
    }
    WebSocketConnectionHolder.mInstance = manager;
  }

  /**
   * Destroy and clear the shared instance.
   */
  public static clear(): void {
    if (WebSocketConnectionHolder.mInstance !== null) {
      Log.d(
        WebSocketConnectionHolder.TAG,
        "clear(): Destroying shared WebSocketManager"
      );
      WebSocketConnectionHolder.mInstance.destroy();
      WebSocketConnectionHolder.mInstance = null;
    }
  }
}
